const COOKIE_WARNING_ACCEPTED_NAME = 'cookie_consent_accepted';
const COOKIE_WARNING_ACCEPTED_VALUE = 'true';
const COOKIE_WARNING_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeHostname(value) {
    return normalizeString(value).toLowerCase().replace(/^\.+/, '');
}

function hasWindowLocation() {
    return typeof window !== 'undefined' && typeof window.location?.origin === 'string';
}

function isLocalOrIpHostname(hostname) {
    const normalizedHostname = normalizeHostname(hostname);

    if (!normalizedHostname) {
        return true;
    }

    if (normalizedHostname === 'localhost' || normalizedHostname === '[::1]' || normalizedHostname === '::1') {
        return true;
    }

    return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(normalizedHostname);
}

function isValidCookieToken(value) {
    return /^[A-Za-z0-9_]+$/.test(value);
}

function decodeCookieComponent(value) {
    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
}

function isValidDomainSegment(value) {
    return /^[a-z0-9-]+$/i.test(value);
}

function isValidDomainName(value) {
    const normalizedDomain = normalizeHostname(value);
    if (!normalizedDomain || normalizedDomain.length > 253 || !normalizedDomain.includes('.')) {
        return false;
    }

    const labels = normalizedDomain.split('.');
    return labels.every((label) => label.length > 0 && label.length <= 63 && isValidDomainSegment(label));
}

function sanitizeCookieDomains(domains) {
    if (!Array.isArray(domains)) {
        return [];
    }

    return domains
        .map((domain) => normalizeHostname(domain))
        .filter((domain, index, values) => isValidDomainName(domain) && values.indexOf(domain) === index)
        .sort((left, right) => right.length - left.length);
}

function doesHostnameMatchDomain(hostname, domain) {
    if (!isValidDomainName(hostname) || !isValidDomainName(domain)) {
        return false;
    }

    return hostname === domain || hostname.endsWith(`.${domain}`);
}

function sanitizeCookieDomain(value) {
    const normalizedDomain = normalizeHostname(value);
    return isValidDomainName(normalizedDomain) ? normalizedDomain : null;
}

export function resolveCookieConcentRootDomain(hostname, { allowedDomains = [], cookieDomain = '' } = {}) {
    const normalizedHostname = normalizeHostname(hostname);

    if (isLocalOrIpHostname(normalizedHostname)) {
        return null;
    }

    const matchedAllowedDomain = sanitizeCookieDomains(allowedDomains).find((domain) =>
        doesHostnameMatchDomain(normalizedHostname, domain),
    );

    if (matchedAllowedDomain) {
        return matchedAllowedDomain;
    }

    const normalizedCookieDomain = sanitizeCookieDomain(cookieDomain);
    return normalizedCookieDomain && doesHostnameMatchDomain(normalizedHostname, normalizedCookieDomain)
        ? normalizedCookieDomain
        : null;
}

export function resolveCookieConcentCookieDomain(hostname, options = {}) {
    const rootDomain = resolveCookieConcentRootDomain(hostname, options);

    return rootDomain ? `.${rootDomain}` : null;
}

export function readCookieValue(cookieName, cookieString = typeof document !== 'undefined' ? document.cookie : '') {
    if (!isValidCookieToken(cookieName)) {
        console.error(new Error(`Invalid cookie name "${cookieName}"`));
        return null;
    }

    const normalizedCookieString = normalizeString(cookieString);
    if (!normalizedCookieString) {
        return null;
    }

    const cookieParts = normalizedCookieString.split(';');
    for (const part of cookieParts) {
        const separatorIndex = part.indexOf('=');
        if (separatorIndex <= 0) {
            continue;
        }

        const name = decodeCookieComponent(part.slice(0, separatorIndex).trim());
        if (name !== cookieName) {
            continue;
        }

        return decodeCookieComponent(part.slice(separatorIndex + 1).trim());
    }

    return null;
}

export function hasAcceptedCookieConcent(cookieString = typeof document !== 'undefined' ? document.cookie : '') {
    return readCookieValue(COOKIE_WARNING_ACCEPTED_NAME, cookieString) === COOKIE_WARNING_ACCEPTED_VALUE;
}

export function resolveCookieConcentUrl({ privacyPolicyUrl, termsFileUrl, termsAndConditionsUrl, currentOrigin }) {
    const fallbackOrigin = hasWindowLocation() ? window.location.origin : '';
    const origin = normalizeString(currentOrigin) || fallbackOrigin;
    const candidates = [privacyPolicyUrl, termsFileUrl, termsAndConditionsUrl];

    for (const candidate of candidates) {
        const normalizedCandidate = normalizeString(candidate);
        if (!normalizedCandidate) {
            continue;
        }

        if (normalizedCandidate.startsWith('/')) {
            if (normalizedCandidate.startsWith('//') || !origin) {
                continue;
            }

            try {
                new URL(normalizedCandidate, origin);
                return normalizedCandidate;
            } catch (error) {
                continue;
            }
        }

        try {
            const parsedUrl = new URL(normalizedCandidate);
            if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
                continue;
            }

            return parsedUrl.toString();
        } catch (error) {
            continue;
        }
    }

    return null;
}

export function isExternalCookieConcentUrl(url) {
    const normalizedUrl = normalizeString(url).toLowerCase();

    return normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://');
}

export function shouldShowCookieConcent(settings, resolvedUrl) {
    return settings?.show_cookies_warning === true && typeof resolvedUrl === 'string' && resolvedUrl.length > 0;
}

export function acceptCookieConcent({
    hostname,
    allowedDomains = [],
    cookieDomain = '',
    documentRef = typeof document !== 'undefined' ? document : null,
} = {}) {
    if (!documentRef || typeof documentRef.cookie !== 'string') {
        console.error(new Error('Cookie concent acceptance requires a browser document.'));
        return false;
    }

    const normalizedHostname = normalizeHostname(hostname);
    const shouldUseHostOnlyCookie = isLocalOrIpHostname(normalizedHostname);
    const resolvedCookieDomain = resolveCookieConcentCookieDomain(normalizedHostname, {
        allowedDomains,
        cookieDomain,
    });

    if (!shouldUseHostOnlyCookie && !resolvedCookieDomain) {
        console.error(new Error(`Cannot resolve shared cookie domain for hostname "${normalizedHostname}"`));
        return false;
    }

    const expiresAt = new Date(Date.now() + COOKIE_WARNING_MAX_AGE_SECONDS * 1000);
    const cookieSegments = [
        `${COOKIE_WARNING_ACCEPTED_NAME}=${COOKIE_WARNING_ACCEPTED_VALUE}`,
        'Path=/',
        `Max-Age=${COOKIE_WARNING_MAX_AGE_SECONDS}`,
        `Expires=${expiresAt.toUTCString()}`,
        'SameSite=Lax',
    ];

    if (resolvedCookieDomain) {
        cookieSegments.splice(1, 0, `Domain=${resolvedCookieDomain}`);
    }

    if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
        cookieSegments.push('Secure');
    }

    try {
        documentRef.cookie = cookieSegments.join('; ');
    } catch (error) {
        console.error(error);
        return false;
    }

    const cookieAccepted = hasAcceptedCookieConcent(documentRef.cookie);
    if (!cookieAccepted) {
        console.error(
            new Error(`Cookie concent acceptance cookie "${COOKIE_WARNING_ACCEPTED_NAME}" was not persisted.`),
        );
    }

    return cookieAccepted;
}

export { COOKIE_WARNING_ACCEPTED_NAME, COOKIE_WARNING_ACCEPTED_VALUE, COOKIE_WARNING_MAX_AGE_SECONDS };
